/** Guarded client evaluation; the shared engine collects all spatial descriptors. */
import { createSpatialBuild } from 'trace-viewer/build/spatial';
import type { SpatialBuildResult, SpatialSceneSnapshot, SpatialExecuteOptions } from 'trace-viewer/build';
export type { SpatialTemplate, SpatialBehaviorDescriptor, SpatialObjectDescriptor, SpatialInteractionDescriptor, SpatialBuildStats, SpatialBuildResult, SpatialSceneSnapshot, SpatialBehaviorType, SpatialExecuteOptions } from 'trace-viewer/build';
import { ENVIRONMENT_PRESETS } from './spatial-environment';
import { instrumentLoopGuards, SHADOWED_GLOBALS, validateSource } from './sandbox-guards';

const MAX_LOOP_ITERATIONS = 100_000;

export function executeSpatialCode(code: string, seed?: number, existingScene?: SpatialSceneSnapshot[], options?: SpatialExecuteOptions): SpatialBuildResult {
    const session = createSpatialBuild(seed, existingScene, { ...options, environmentPresets: ENVIRONMENT_PRESETS });
    const violations = validateSource(code);
    if (violations.length) return session.result(violations);
    const instrumentedCode = instrumentLoopGuards(code, MAX_LOOP_ITERATIONS);
    const wrappedSource = `
'use strict';
var __loopCount = 0;
{
${instrumentedCode}
}
`;
    try {
        const names = Object.keys(session.api);
        // eslint-disable-next-line @typescript-eslint/no-implied-eval -- guarded client DSL execution
        const fn = new Function(...SHADOWED_GLOBALS, ...names, wrappedSource);
        (fn as (...args: unknown[]) => void)(...SHADOWED_GLOBALS.map(() => undefined), ...names.map(name => session.api[name]));
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return session.result([`${message}${shadowedHelper(message, code, Object.keys(session.api))}`]);
    }
    return session.result();
}

/**
 * Why a helper call failed when the code declares a name of its own over it (`const seat = clone("Seat")`). The
 * declaration is matched in the raw source, so one inside a comment or string also counts: the hint only ever
 * follows that helper's own call failing, where such a declaration is the likely cause anyway.
 */
function shadowedHelper(message: string, code: string, helpers: readonly string[]): string {
    const name = /^(\w+) is not a function|^Cannot access '(\w+)' before initialization/.exec(message)?.slice(1).find(Boolean);
    if (!name || !helpers.includes(name) || !new RegExp(`\\b(?:const|let|var|function|class)\\s+${name}\\b`).test(code)) return '';
    return `: your code declares its own \`${name}\`, which hides the build helper ${name}(); rename yours.`;
}
